import React, { useEffect, useState } from "react";
import { Save, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Loader } from "@/components/dashboard/ui";
import { ImageField } from "@/components/admin/MediaPicker";
import { API } from "@/lib/api";

const VERSIONS = [
  ["main", "Logo principal"], ["header", "En-tête (bureau)"], ["mobile", "Mobile"], ["light", "Version claire"],
  ["dark", "Version sombre"], ["footer", "Pied de page"], ["login", "Page de connexion"], ["admin", "Tableau de bord admin"], ["favicon", "Favicon"],
];
const SIZES = [
  ["header_width", "Hauteur en-tête (px)"], ["mobile_width", "Hauteur mobile (px)"], ["footer_width", "Hauteur pied de page (px)"],
  ["login_width", "Hauteur connexion (px)"], ["admin_width", "Hauteur admin (px)"],
];

export default function AdminLogo() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/website/logo").then(({ data }) => setCfg(data)); }, []);
  const setVersion = (k, v) => setCfg((c) => ({ ...c, versions: { ...c.versions, [k]: v } }));
  const setSize = (k, v) => setCfg((c) => ({ ...c, sizes: { ...c.sizes, [k]: v } }));
  const save = async () => { await api.put("/website/logo", cfg); toast.success("Logo enregistré"); };
  const resetSizes = async () => { const { data } = await api.post("/website/logo/reset-sizes"); setCfg(data); toast.success("Dimensions par défaut restaurées"); };
  if (cfg === null) return <Loader />;
  const previewSrc = cfg.versions?.header ? `${API}/media/file/${cfg.versions.header}` : "/delya-logo.png";
  return (
    <div>
      <PageHeader title="Gestion du logo" subtitle="Configurez les différentes versions et tailles du logo, indépendamment."
        action={<Button className="bg-primary" onClick={save} data-testid="logo-save"><Save className="h-4 w-4 mr-1.5" />Enregistrer</Button>} />

      <div className="mb-6 bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="logo-preview">
        <div className="px-4 py-2 border-b border-slate-100 text-xs font-semibold text-slate-400 uppercase tracking-wider">Aperçu de l'en-tête (la hauteur s'adapte au logo)</div>
        <div className="bg-white">
          <div className="max-w-5xl mx-auto px-6 min-h-16 py-2.5 flex items-center justify-between gap-4 border-b border-slate-100">
            <img src={previewSrc} alt="Logo" className="object-contain" style={{ height: `${cfg.sizes?.header_width || 48}px`, width: "auto", maxWidth: "60%", maxHeight: "30vh" }} />
            <div className="hidden sm:flex items-center gap-4 text-sm text-slate-500">
              <span>Accueil</span><span>Services</span><span>Contact</span>
              <span className="px-3 py-1.5 rounded-md bg-primary text-white text-xs">Faire une demande</span>
            </div>
          </div>
          <div className="p-4 text-xs text-slate-400">Hauteur du logo : {cfg.sizes?.header_width || 48}px · l'en-tête grandit automatiquement, sans débordement.</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Versions du logo</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {VERSIONS.map(([k, label]) => <ImageField key={k} label={label} value={cfg.versions?.[k]} onChange={(v) => setVersion(k, v)} />)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Dimensions</h3>
            <Button size="sm" variant="outline" onClick={resetSizes} data-testid="logo-reset-sizes"><RotateCcw className="h-4 w-4 mr-1.5" />Restaurer les dimensions par défaut</Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {SIZES.map(([k, label]) => (
              <div key={k}><Label className="text-xs">{label}</Label><Input type="number" value={cfg.sizes?.[k] ?? ""} onChange={(e) => setSize(k, parseInt(e.target.value) || 0)} className="mt-1" data-testid={`logo-size-${k}`} /></div>
            ))}
          </div>
          <label className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100"><span className="text-sm">Conserver les proportions</span><Switch checked={cfg.sizes?.preserve_ratio !== false} onCheckedChange={(v) => setSize("preserve_ratio", v)} /></label>
          <p className="mt-4 text-xs text-slate-500 bg-slate-50 rounded-lg p-3 border border-slate-100">Si aucune version « en-tête » n'est définie, le logo Delya par défaut est utilisé. Les images restent responsives sur mobile.</p>
        </div>
      </div>
    </div>
  );
}
